// src/components/Sidebar.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Home, Package, History, PlusCircle, LogOut,
  User, Bell, ChevronRight, Calendar, Users, FileText,
  FolderOpen, ClipboardList, Box, ShieldCheck, UserCheck,
  LayoutDashboard, Car, PackageOpen, Wrench, ClipboardCheck,
  CheckCircle, ChevronDown, UsersIcon, CarFront, Menu, X
} from 'lucide-react';
import logo from '../images/logo.jpg';

export default function Sidebar({ onMenuClick, isCollapsed: externalCollapsed, onToggleCollapse }) {
  const [internalCollapsed, setInternalCollapsed] = useState(false);
  const [openSubmenus, setOpenSubmenus] = useState({});
  const [activeMenu, setActiveMenu] = useState('');
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  // Détection mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setMobileOpen(false);
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Utiliser la prop externe si fournie, sinon l'état interne
  const isCollapsed = useMemo(() => {
    if (isMobile && !mobileOpen) return true;
    return externalCollapsed !== undefined ? externalCollapsed : internalCollapsed;
  }, [externalCollapsed, internalCollapsed, isMobile, mobileOpen]);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userNom = user.nom || 'Utilisateur';
  const userRole = user.role || 'client';
  const userInitial = userNom.charAt(0).toUpperCase();

  // Configuration des menus par rôle
 const menus = useMemo(() => ({
  admin: [
    { to: '/rapports', label: 'Tableau de bord', icon: LayoutDashboard },
    {
      type: 'submenu',
      key: 'evenements',
      label: 'Gestion des Événements',
      icon: Calendar,
      items: [
        { to: '/admin/evenements', label: 'Toutes les demandes', icon: FileText },
        { to: '/admin/beneficiaires', label: 'Bénéficiaires', icon: ShieldCheck },
        { to: '/admin/packs-enterrement', label: 'Packs enterrement', icon: PackageOpen },
        { to: '/admin/evenements/historique', label: 'Historique', icon: History },
      ],
    },
    {
      type: 'submenu',
      key: 'vehicules',
      label: 'Gestion des Véhicules',
      icon: Car,
      items: [
        { to: '/vehicules', label: 'Liste des véhicules', icon: CarFront },
        { to: '/vehicules/nouveau', label: 'Ajouter véhicule', icon: PlusCircle },
        { to: '/demandes-reparation', label: 'Demandes validées', icon: ClipboardCheck },
      ],
    },
    {
      type: 'submenu',
      key: 'materiels',
      label: 'Gestion des Matériels',
      icon: Package,
      items: [
        { to: '/admin/materiels', label: 'Liste des matériels', icon: Box },
        { to: '/admin/categories', label: 'Catégories', icon: FolderOpen },
        { to: '/historiques', label: 'Historique', icon: History },
      ],
    },
    {
      type: 'submenu',
      key: 'utilisateurs',
      label: 'Gestion des Utilisateurs',
      icon: Users,
      items: [
        { to: '/admin/users', label: 'Liste des utilisateurs', icon: Users },
      ],
    },
    { to: '/admin/conges', label: 'Gestion des Congés', icon: Calendar },
  ],
  validateur: [
    { to: '/rapports', label: 'Tableau de bord', icon: LayoutDashboard },
    {
      type: 'submenu',
      key: 'evenements',
      label: 'Gestion des Événements',
      icon: Calendar,
      items: [
        { to: '/validateur/validation-evenements', label: 'Validation des demandes', icon: FileText },
      ],
    },
    {
      type: 'submenu',
      key: 'vehicules',
      label: 'Gestion des Véhicules',
      icon: Car,
      items: [
        { to: '/validations/nouvelle', label: 'Créer réparation', icon: CheckCircle },
        { to: '/demandes-reparation/nouvelle', label: 'Demandes de réparation', icon: ClipboardList },
        { to: '/reparations', label: 'Réparations', icon: Wrench },
      ],
    },
    {
      type: 'submenu',
      key: 'materiels',
      label: 'Gestion des Matériels',
      icon: Package,
      items: [
        { to: '/validateur', label: 'Validation des demandes', icon: Package },
        { to: '/mes-demandes', label: 'Mes demandes', icon: Package },
      ],
    },
    {
      type: 'submenu',
      key: 'conges',
      label: 'Gestion des Congés',
      icon: Calendar,
      items: [
        { to: '/conges/nouveau', label: 'Demander congé', icon: PlusCircle },
        { to: '/mes-conges', label: 'Mes congés', icon: Calendar },
        { to: '/validateur/conges', label: 'Validation des congés', icon: Calendar },
      ],
    }
  ],
  magasinier: [
    { to: '/rapports', label: 'Tableau de bord', icon: LayoutDashboard },
    {
      type: 'submenu',
      key: 'evenements',
      label: 'Gestion des Événements',
      icon: Calendar,
      items: [
        { to: '/magasinier/livraisons', label: 'Liste des livraisons', icon: FileText },
      ],
    },
    {
      type: 'submenu',
      key: 'vehicules',
      label: 'Gestion des Véhicules',
      icon: Car,
      items: [
        { to: '/vehicules', label: 'Liste des véhicules', icon: CarFront },
        { to: '/demandes-reparation', label: 'Demandes validées', icon: ClipboardCheck },
        { to: '/reparations', label: 'Réparations', icon: Wrench },
        { to: '/pieces', label: 'Pièces', icon: PackageOpen },
      ],
    },
    {
      type: 'submenu',
      key: 'conges',
      label: 'Gestion des Congés',
      icon: Calendar,
      items: [
        { to: '/conges/nouveau', label: 'Demander congé', icon: PlusCircle },
        { to: '/mes-conges', label: 'Mes congés', icon: Calendar },
      ],
    },
    {
      type: 'submenu',
      key: 'materiels',
      label: 'Gestion des Matériels',
      icon: Package,
      items: [
        { to: '/magasin', label: 'Gestion du stock', icon: Box },
        { to: '/mes-demandes', label: 'Mes demandes', icon: Package },
      ],
    }
  ],
  client: [
    { to: '/', label: 'Accueil', icon: Home },
    {
      type: 'submenu',
      key: 'evenements',
      label: 'Gestion des Événements',
      icon: Calendar,
      items: [
        { to: '/client/mes-evenements', label: 'Mes demandes', icon: FileText },
        { to: '/client/evenements/nouveau', label: 'Nouvelle demande', icon: PlusCircle },
      ],
    },
    {
      type: 'submenu',
      key: 'vehicules',
      label: 'Gestion des Véhicules',
      icon: Car,
      items: [
        { to: '/demandes-reparation/nouvelle', label: 'Demande réparation', icon: ClipboardList },
      ],
    },
    {
      type: 'submenu',
      key: 'materiels',
      label: 'Gestion des Matériels',
      icon: Package,
      items: [
        { to: '/demandes/nouvelle', label: 'Nouvelle demande', icon: PlusCircle },
        { to: '/mes-demandes', label: 'Mes demandes', icon: FileText },
      ],
    },
    {
      type: 'submenu',
      key: 'conges',
      label: 'Gestion des Congés',
      icon: Calendar,
      items: [
        { to: '/conges/nouveau', label: 'Demander congé', icon: PlusCircle },
        { to: '/mes-conges', label: 'Mes congés', icon: FileText },
      ],
    },
  ],
  chef_equipe: [
    { to: '/rapports', label: 'Tableau de bord', icon: LayoutDashboard },
    {
      type: 'submenu',
      key: 'vehicules',
      label: 'Gestion des Véhicules',
      icon: Car,
      items: [
        { to: '/vehicules', label: 'Liste des véhicules', icon: CarFront },
        { to: '/vehicules/nouveau', label: 'Ajouter véhicule', icon: PlusCircle },
        { to: '/demandes-reparation', label: 'Demandes validées', icon: ClipboardCheck },
        { to: '/validations/nouvelle', label: 'Validation réparations', icon: CheckCircle },
        { to: '/reparations', label: 'Réparations', icon: Wrench },
      ],
    },
    {
      type: 'submenu',
      key: 'conges',
      label: 'Gestion des Congés',
      icon: Calendar,
      items: [
        { to: '/conges/nouveau', label: 'Demander congé', icon: PlusCircle },
        { to: '/chef/conges', label: 'Validation des congés', icon: UserCheck },
        { to: '/mes-conges', label: 'Mes congés', icon: FileText },
      ],
    },
    {
      type: 'submenu',
      key: 'materiels',
      label: 'Gestion des Matériels',
      icon: UsersIcon,
      items: [
        { to: '/mes-demandes', label: 'Mes demandes', icon: Package },
      ],
    },
  ],
  mecanicien: [
    { to: '/', label: 'Accueil', icon: Home },
    {
      type: 'submenu',
      key: 'vehicules',
      label: 'Gestion des Véhicules',
      icon: UsersIcon,
      items: [
        { to: '/vehicules', label: 'Liste des véhicules', icon: CarFront },
        { to: '/demandes-reparation/nouvelle', label: 'Demande réparation', icon: PlusCircle },
        { to: '/reparations/nouvelle', label: 'Nouvelle réparation', icon: PlusCircle },
        { to: '/demandes-reparation', label: 'Demandes validées', icon: ClipboardCheck },
        { to: '/reparations', label: 'Liste des réparations', icon: Wrench },
        { to: '/pieces', label: 'Pièces', icon: PackageOpen },
      ],
    },
    {
      type: 'submenu',
      key: 'materiels',
      label: 'Gestion des Matériels',
      icon: User,
      items: [
        { to: '/mes-demandes', label: 'Mes demandes', icon: FileText },
      ],
    },
    {
      type: 'submenu',
      key: 'conges',
      label: 'Gestion des Congés',
      icon: User,
      items: [
        { to: '/conges/nouveau', label: 'Demander congé', icon: PlusCircle },
        { to: '/mes-conges', label: 'Mes congés', icon: FileText }
      ],
    },
  ],
}), []);

  // Détecter le menu actif au chargement
  useEffect(() => {
    const currentPath = location.pathname;
    const userMenu = menus[userRole] || menus.client;
    
    userMenu.forEach(item => {
      if (item.to === currentPath || currentPath.startsWith(item.to + '/')) {
        setActiveMenu(item.label);
      }
      if (item.type === 'submenu') {
        item.items.forEach(subItem => {
          if (subItem.to === currentPath || currentPath.startsWith(subItem.to + '/')) {
            setActiveMenu(subItem.label);
            setOpenSubmenus(prev => ({ ...prev, [item.key]: true }));
          }
        });
      }
    });
  }, [location.pathname, userRole, menus]);

  const getNavItems = () => menus[userRole] || menus.client;
  const navItems = getNavItems();

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const toggleSubmenu = (key) => {
    setOpenSubmenus(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleMenuClick = (label, to) => {
    setActiveMenu(label);
    
    if (onMenuClick) {
      onMenuClick(label);
    }
    
    // Fermer le menu mobile après clic
    if (isMobile) {
      setMobileOpen(false);
    }
    
    // Fermer les sous-menus en mode collapsed
    if (isCollapsed) {
      setOpenSubmenus({});
    }
    
    // Navigation
    if (location.pathname !== to) {
      navigate(to);
    }
  };

  const handleToggleCollapse = () => {
    if (onToggleCollapse) {
      onToggleCollapse(!isCollapsed);
    } else {
      setInternalCollapsed(!isCollapsed);
    }
    
    // Fermer tous les sous-menus quand on collapse
    if (!isCollapsed) {
      setOpenSubmenus({});
    }
  };

  const getRoleColor = (role) => {
    const colors = {
      'admin': 'bg-red-100 text-red-700',
      'client': 'bg-blue-100 text-blue-700',
      'validateur': 'bg-green-100 text-green-700',
      'chef_equipe': 'bg-purple-100 text-purple-700',
      'magasinier': 'bg-orange-100 text-orange-700',
      'mecanicien': 'bg-cyan-100 text-cyan-700'
    };
    return colors[role] || 'bg-gray-100 text-gray-700';
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login', { replace: true });
  };

  // Menu pour mobile (bouton hamburger)
  const MobileMenuButton = () => (
    <button
      onClick={() => setMobileOpen(!mobileOpen)}
      className="fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-md md:hidden"
    >
      {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
    </button>
  );

  return (
    <>
      {/* Bouton menu mobile */}
      <MobileMenuButton />

      {/* Overlay pour mobile */}
      {isMobile && mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed left-0 top-0 h-screen bg-white border-r border-gray-200 transition-all duration-300 z-40 ${
        isMobile 
          ? mobileOpen 
            ? 'w-64 translate-x-0' 
            : '-translate-x-full'
          : isCollapsed 
            ? 'w-20' 
            : 'w-64'
      }`}>
        {/* Sidebar Header avec logo */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className={`flex items-center ${isCollapsed ? 'justify-center w-full' : 'space-x-3'}`}>
            <div className="w-8 h-8 rounded-lg overflow-hidden">
              <img src={logo} alt="Logo" className="w-full h-full object-cover" />
            </div>
            {!isCollapsed && (
              <span className="font-semibold text-gray-900">CR Mansouria</span>
            )}
          </div>

          {!isMobile && (
            <button
              onClick={handleToggleCollapse}
              className="p-1.5 rounded-md hover:bg-gray-100 transition-colors"
              title={isCollapsed ? "Développer" : "Réduire"}
            >
              <ChevronRight className={`w-4 h-4 text-gray-500 transition-transform ${
                isCollapsed ? 'rotate-180' : ''
              }`} />
            </button>
          )}
        </div>

       
        {/* Navigation Menu */}
        <nav className="flex-1 overflow-y-auto py-4 custom-scrollbar">
          <div className="space-y-1 px-3">
            {navItems.map((item) => {
              if (item.type === 'submenu') {
                const isOpen = openSubmenus[item.key];
                const hasActiveChild = item.items.some(subItem => isActive(subItem.to));
                
                // En mode collapsed, on montre juste l'icône avec un tooltip
                if (isCollapsed) {
                  return (
                    <div key={item.key} className="relative group">
                      <button
                        onClick={() => handleMenuClick(item.label, item.items[0]?.to || '/')}
                        className={`flex items-center justify-center w-full p-3 rounded-lg text-sm transition-colors ${
                          hasActiveChild
                            ? 'bg-blue-50 text-blue-600 font-medium'
                            : 'text-gray-600 hover: hover:text-gray-900'
                        }`}
                      >
                        <item.icon className="w-5 h-5" />
                      </button>
                      
                      {/* Tooltip pour mode collapsed */}
                      <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-3 py-1.5 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">
                        {item.label}
                      </div>
                    </div>
                  );
                }
                
                // Mode expanded - menu normal
                return (
                  <div key={item.key} className="space-y-1">
                    <button
                      onClick={() => toggleSubmenu(item.key)}
                      className={`flex items-center justify-between w-full p-2.5 rounded-lg text-sm transition-colors ${
                        hasActiveChild || isOpen
                          ? 'bg-blue-50 text-blue-600 font-medium'
                          : 'text-gray-600 hover: hover:text-gray-900'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <item.icon className="w-4 h-4" />
                        <span>{item.label}</span>
                      </div>
                      <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                    </button>
                    
                    {isOpen && (
                      <div className="ml-4 pl-3 border-l border-gray-200 space-y-1">
                        {item.items.map((subItem) => (
                          <button
                            key={subItem.to}
                            onClick={() => handleMenuClick(subItem.label, subItem.to)}
                            className={`flex items-center space-x-3 w-full p-2 rounded-md text-sm transition-colors ${
                              isActive(subItem.to)
                                ? 'text-blue-600 bg-blue-50 font-medium'
                                : 'text-gray-600 hover: hover:text-gray-900'
                            }`}
                          >
                            <subItem.icon className="w-3.5 h-3.5" />
                            <span>{subItem.label}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              // Item simple (sans sous-menu)
              return (
                <div key={item.to} className="relative group">
                  <button
                    onClick={() => handleMenuClick(item.label, item.to)}
                    className={`flex items-center w-full p-2.5 rounded-lg text-sm transition-colors ${
                      isActive(item.to)
                        ? 'bg-blue-50 text-blue-600 font-medium'
                        : 'text-gray-600 hover: hover:text-gray-900'
                    } ${isCollapsed ? 'justify-center' : 'space-x-3'}`}
                  >
                    <item.icon className="w-4 h-4" />
                    {!isCollapsed && <span>{item.label}</span>}
                  </button>
                  
                  {/* Tooltip pour mode collapsed */}
                  {isCollapsed && (
                    <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-3 py-1.5 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">
                      {item.label}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </nav>

        {/* Bottom Actions */}
        <div className="p-4 border-t border-gray-200">
          <div className={`space-y-1 ${isCollapsed ? 'flex flex-col items-center' : ''}`}>
            <div className="relative group">
              <button
                onClick={() => handleMenuClick('Profil', '/profile')}
                className={`flex items-center w-full p-2.5 rounded-lg text-sm transition-colors ${
                  isActive('/profile')
                    ? 'text-blue-600 bg-blue-50 font-medium'
                    : 'text-gray-600 hover: hover:text-gray-900'
                } ${isCollapsed ? 'justify-center' : 'space-x-3'}`}
              >
                <User className="w-4 h-4" />
                {!isCollapsed && <span>Profil</span>}
              </button>
              
              {isCollapsed && (
                <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-3 py-1.5 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">
                  Profil
                </div>
              )}
            </div>
          </div>

          <div className="relative group mt-4">
            <button
              onClick={() => setShowLogoutConfirm(true)}
              className={`flex items-center w-full p-2.5 rounded-lg text-sm text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors ${
                isCollapsed ? 'justify-center' : 'space-x-3'
              }`}
            >
              <LogOut className="w-4 h-4" />
              {!isCollapsed && <span>Déconnexion</span>}
            </button>
            
            {isCollapsed && (
              <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-3 py-1.5 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">
                Déconnexion
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Confirmation Modal pour Logout */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black/30"
            onClick={() => setShowLogoutConfirm(false)}
          />
          
          <div className="relative bg-white rounded-lg shadow-lg max-w-sm w-full p-6">
            <div className="mx-auto w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <LogOut className="w-6 h-6 text-red-600" />
            </div>
            
            <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
              Déconnexion
            </h3>
            
            <p className="text-gray-600 text-center text-sm mb-6">
              Êtes-vous sûr de vouloir vous déconnecter ?
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowLogoutConfirm(false)}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleLogout}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors"
              >
                Se déconnecter
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}